import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { IoArrowBack } from 'react-icons/io5';
import css from './ModuleBreadCrumbHeader.module.css';
import { trackFamilyProgramEvent } from '../../util/mixpanelHelpers';
import { MIXPANEL_EVENTS } from '../../util/enums';
import { fetchTutorialsSubcategoriesData, fetchTutorialSubcategories } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../helpers/dataDupe';
import { getLocalizedRoutePath, navigateBackOrFallback } from '../../util/backNavigation';

const ModuleBreadCrumbHeader = ({ articleTitle, isFamilyProgram, selectedModule }) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const moduleIdentifier = intl.formatMessage({
    id: 'RenderOtherModules.moduleIdentifier',
    defaultMessage: 'Modul',
  });

  const { tutorialsSubcategories, fetchTutorialsSubcategoriesInProgress } =
    useSelector(fetchTutorialsSubcategoriesData) || {};

  // Fetch subcategories only once
  useEffect(() => {
    if (!isArrayLength(tutorialsSubcategories)) {
      dispatch(fetchTutorialSubcategories({}));
    }
  }, [dispatch, tutorialsSubcategories]);

  // Filter menopause/family modules
  const menopauseTutorials = useMemo(() => {
    if (!isArrayLength(tutorialsSubcategories)) return [];
    return tutorialsSubcategories.filter(t =>
      isFamilyProgram
        ? !t?.attributes?.title?.includes(moduleIdentifier) && t?.attributes?.description
        : t?.attributes?.title?.includes(moduleIdentifier)
    );
  }, [tutorialsSubcategories, isFamilyProgram, moduleIdentifier]);

  const selectedTutorial = menopauseTutorials.filter(
    tutorial => tutorial?.attributes.slug === selectedModule
  );

  const fallbackRouteName = isFamilyProgram ? 'FamilyProgramPage' : 'MenopauseResourcePage';
  const fallbackPath = getLocalizedRoutePath(fallbackRouteName, location.pathname);

  const handleHomeClick = () => {
    if (isFamilyProgram) {
      trackFamilyProgramEvent(MIXPANEL_EVENTS?.FAMILY_NAV_CLICKED);
    }

    history.push(fallbackPath);
  };

  const handleBackClick = () => {
    navigateBackOrFallback(history, fallbackPath);
  };

  return (
    <div className={css.root}>
      <div className={css.breadCrumbContainer}>
        <button
          className={css.backButton}
          onClick={handleBackClick}
          aria-label={intl.formatMessage({ id: 'guide.goBack', defaultMessage: 'Back' })}
          type="button"
        >
          <IoArrowBack size={16} />
          <span className={css.backLabel}>{intl.formatMessage({ id: 'guide.goBack' })}</span>
        </button>
        <div className={css.breadcrumbs}>
          <span className={css.parentLabel} onClick={handleHomeClick}>
            {!isFamilyProgram
              ? intl.formatMessage({ id: 'RenderHomepage.menopauseProgramTitle' })
              : intl.formatMessage({ id: 'RenderHomepage.familyProgramTitle' })}
          </span>
          <span className={css.separator}>/</span>
          <span className={css.articleTitle}>
            {articleTitle ? articleTitle : selectedTutorial[0]?.attributes?.title}
          </span>
        </div>
      </div>
    </div>
  );
};

ModuleBreadCrumbHeader.propTypes = {
  articleTitle: PropTypes.string,
  isFamilyProgram: PropTypes.bool,
  selectedModule: PropTypes.string,
};

export default ModuleBreadCrumbHeader;
